import { IStrikeZoneLocation, StrikeZoneLocation } from 'interfaces/session/IStrikeZoneDefinition';
import { IPlaneConfiguration } from 'interfaces/session/IStrikeZonePlaneConfiguration';
import { StrikeZoneCalculationInformations } from 'interfaces/team/IApiScheduleResponse';

const CENTIMETERS_PER_INCH = 2.54;

export const PLANE_ORDER: StrikeZoneLocation[] = [StrikeZoneLocation.Front, StrikeZoneLocation.Middle, StrikeZoneLocation.Back];

const roundToTwoDecimals = (value: number): number => Math.round(value * 100) / 100;

export const inchesToCentimeters = (inches: number): number => roundToTwoDecimals(inches * CENTIMETERS_PER_INCH);

export const centimetersToInches = (centimeters: number): number => roundToTwoDecimals(centimeters / CENTIMETERS_PER_INCH);

export const sanitizeNumericInput = (raw: string): string => {
  const withDot = raw.replace(/,/g, '.');
  const sign = withDot.startsWith('-') ? '-' : '';
  const [integerPart, ...fractionParts] = withDot.replace(/[^\d.]/g, '').split('.');
  const hasSeparator = fractionParts.length > 0;

  if (!hasSeparator) {
    return `${sign}${integerPart}`;
  }

  return `${sign}${integerPart === '' ? '0' : integerPart}.${fractionParts.join('')}`;
};

export const parseNumericInput = (raw: string): number | null => {
  const trimmed = raw.trim();

  if (trimmed === '') {
    return 0;
  }

  if (!/^-?\d*\.?\d+$/.test(trimmed)) {
    return null;
  }

  return Number(trimmed);
};

export const createDefaultPlaneConfiguration = (): IPlaneConfiguration[] =>
  PLANE_ORDER.map((locationType) => ({
    locationType,
    isEnabled: locationType === StrikeZoneLocation.Front,
    isOffsetEnabled: false,
    offsetY: 0,
    isExtensionEnabled: false,
    extension: 0,
  }));

export const mapCalculationsToPlaneConfiguration = (calculations?: StrikeZoneCalculationInformations[]): IPlaneConfiguration[] => {
  if (!calculations || calculations.length === 0) {
    return createDefaultPlaneConfiguration();
  }

  return PLANE_ORDER.map((locationType) => {
    const calculation = calculations.find((item) => item.strikeZoneCalculationType === locationType);

    if (!calculation) {
      return {
        locationType,
        isEnabled: false,
        isOffsetEnabled: false,
        offsetY: 0,
        isExtensionEnabled: false,
        extension: 0,
      };
    }

    const offsetY = Number(calculation.offset ?? 0);
    const extension = Number(calculation.extension ?? 0);

    return {
      locationType,
      isEnabled: true,
      isOffsetEnabled: offsetY !== 0,
      offsetY,
      isExtensionEnabled: extension !== 0,
      extension,
    };
  });
};

export const mapPlaneConfigurationToLocations = (planes: IPlaneConfiguration[]): IStrikeZoneLocation[] =>
  planes
    .filter((item) => item.isEnabled)
    .map((item) => ({
      locationType: item.locationType,
      offsetY: item.isOffsetEnabled ? item.offsetY : 0,
      bottomExtension: item.isExtensionEnabled ? item.extension : 0,
    }));

export const mapCalculationsToLocations = (
  calculations?: StrikeZoneCalculationInformations[]
): IStrikeZoneLocation[] | undefined =>
  !calculations || calculations.length === 0
    ? undefined
    : mapPlaneConfigurationToLocations(mapCalculationsToPlaneConfiguration(calculations));

export const PLANE_SECTION_NAMES: Record<StrikeZoneLocation, string> = {
  [StrikeZoneLocation.Front]: 'front',
  [StrikeZoneLocation.Middle]: 'middle',
  [StrikeZoneLocation.Back]: 'back',
};

const SECTION_COUNT_WORDS: { [count: number]: string } = {
  1: 'One',
  2: 'Two',
  3: 'Three',
};

export const getEnabledPlaneLocations = (planes: IPlaneConfiguration[]): StrikeZoneLocation[] =>
  PLANE_ORDER.filter((locationType) => planes.some((plane) => plane.locationType === locationType && plane.isEnabled));

export const formatSectionCountLabel = (count: number): string => {
  const word = SECTION_COUNT_WORDS[count];

  return word ? `${word} section SZ` : '';
};

export const formatSectionsDescription = (locations: StrikeZoneLocation[]): string => {
  const names = locations.map((locationType) => PLANE_SECTION_NAMES[locationType]);

  if (names.length === 0) {
    return '';
  }

  if (names.length === 1) {
    return `The ${names[0]} section of the home plate determine pitches.`;
  }

  if (names.length === 2) {
    return `The ${names[0]} and ${names[1]} sections of the home plate determine pitches.`;
  }

  return `The ${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]} sections of the home plate determine pitches.`;
};
